import type { FC, ReactNode } from 'react'
import { useEffect } from 'react'

import { AuthClient, decodeToken } from '@skytech/auth'
import { config } from '@skytech/config'
import { AppWithMonitoring } from '@skytech/monitoring'
import { CookieConsent, PageLoader } from '@skytech/ui'
import {
  checkLocaleAgainstTranslations,
  DEFAULT_LOCALE,
  getBrowserLocale,
  getDefaultCountryId,
  getWhiteLabelBasedOnId,
  getWhiteLabelForDealerStyle,
} from '@skytech/utils'
import { QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill'
import { IntlProvider } from 'react-intl'
import { Provider as ReduxProvider, useDispatch } from 'react-redux'
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { createStore, applyMiddleware, compose } from 'redux'
import { thunk } from 'redux-thunk'

import queries from '@/api/queries'
import translationResources from '@/api/translations'
import userResources from '@/api/user'
import { AppContextProvider, useAppContext } from '@/App.context'
import Login from '@/components/login/Login'
import Logout from '@/components/Logout'
import { setLocale as setReduxLocale, setMessages } from '@/ducks/i18nReducer'
import useLogIn from '@/hooks/useLogin'
import type { TokenData } from '@/types'
import getUserFromToken from '@/util/getUserFromToken'
import { appRoutes } from '@/util/misc'

import Pages from './components/content/Pages'
import Footer from './components/footer/Footer'
import { Header } from './components/header/Header'
import { useTranslationWithJSXSupport } from './components/i18n/hooks'
import Navigation from './components/navigation/Navigation'
import SubNavigation from './components/navigation/SubNavigation'
import Popup from './components/util/Popup'
import reducer from './ducks'
import useMetaTags from './hooks/meta-tags'
import { shopTempMiddleware } from './middlewares/shopTempOrderMiddleware'
import { queryClient } from './queryClient'

import './sass/body.scss'
import './sass/app.scss'

const AuthenticatedPages: FC = () => {
  const { setLocale, setWhiteLabel, whiteLabel } = useAppContext()
  const { data: translationList } = useQuery(
    queries.translationList(),
    translationResources.getList,
    { staleTime: Infinity }
  )

  const { data: user, isLoading: isUserLoading } = useQuery(queries.user(), userResources.get, {
    retry: 0,
    staleTime: Infinity,
    onSuccess(user) {
      setWhiteLabel(getWhiteLabelBasedOnId(user.whiteLabelId))
    },
    onError() {
      AuthClient.logOut({ isTriggeredProgrammatically: true })
    },
  })

  const { data: profile, isLoading: isProfileLoading } = useQuery(
    queries.profile(),
    userResources.getProfile,
    {
      enabled: Boolean(user),
      staleTime: Infinity,
      onSuccess(profile) {
        if (!translationList) return
        const browserLocale = getBrowserLocale()
        const updatedLocale = checkLocaleAgainstTranslations({
          locale: profile?.locale || browserLocale,
          translationList,
          defaultLocale: DEFAULT_LOCALE,
        })
        setLocale(updatedLocale)
        // todo: remove as soon as all the dependencies are refactored
        setReduxLocale(updatedLocale)
      },
    }
  )

  if (isUserLoading || !user || isProfileLoading || !profile)
    return <PageLoader whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)} />

  return (
    <div className={`app wl-${whiteLabel}`}>
      <Header />
      <Navigation />
      <SubNavigation />
      <main>
        <Pages />
      </main>
      <Footer />
      <ToastContainer className="toast-container" />
      <Popup />
    </div>
  )
}

const AppCore: FC = () => {
  polyfillCountryFlagEmojis()

  const translateWithJSXSupport = useTranslationWithJSXSupport()
  const location = useLocation()
  const navigate = useNavigate()
  const client = useQueryClient()
  const { locale, whiteLabel } = useAppContext()

  useMetaTags()

  const loginMutation = useLogIn({ isUrlCredentials: true })

  const searchParams = new URLSearchParams(location.search)
  const urlToken = searchParams.get('token')
  const urlSessionId = searchParams.get('sess-id')
  const { setWhiteLabel } = useAppContext()

  useEffect(() => {
    if (urlToken) {
      AuthClient.setToken(urlToken)

      const tokenData: TokenData = decodeToken(urlToken)
      const user = getUserFromToken(tokenData)

      client.setQueryData(queries.user(), user)
      if (!user) return
      // Needs to be set manually as the hook has not been fired yet
      setWhiteLabel(getWhiteLabelBasedOnId(user.whiteLabelId))

      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      navigate(url)
    }
  }, [client, navigate, setWhiteLabel, urlToken])

  useEffect(() => {
    if (urlSessionId) {
      const defaultPhoneCountryId = getDefaultCountryId(locale)
      loginMutation.mutate({ sessionId: urlSessionId, phoneCountryId: defaultPhoneCountryId })
    }
  }, [locale, loginMutation, urlSessionId])

  if (loginMutation.isLoading || urlToken || urlSessionId)
    return <PageLoader whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)} />

  const isDevEnv = config.ENVIRONMENT === 'dev'

  return (
    <>
      <Routes>
        <Route path={appRoutes.login} element={<Login />} />
        <Route path={appRoutes.logout} element={<Logout />} />

        <Route path="*" element={<AuthenticatedPages />} />
      </Routes>

      {!isDevEnv && (
        <CookieConsent
          onRejectCookies={() => {
            if (!AuthClient.getToken()) return
            AuthClient.logOut({ isTriggeredProgrammatically: true })
          }}
          onTranslate={translateWithJSXSupport}
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        />
      )}
    </>
  )
}

const AppIntl: FC<{ children: ReactNode }> = ({ children }) => {
  const { locale, whiteLabel } = useAppContext()
  const dispatch = useDispatch()

  const { data: translations, isLoading: isTranslationsLoading } = useQuery(
    queries.translationList(),
    translationResources.getList,
    {
      staleTime: Infinity,
      onSuccess(data) {
        // todo: remove as soon as all the dependencies are refactored
        dispatch(setMessages(data))
      },
    }
  )

  if (isTranslationsLoading)
    return <PageLoader whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)} />
  if (!translations) throw new Error('Kunne ikke laste oversettelser.')

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={DEFAULT_LOCALE}
      messages={translations[locale]}
      // Do not show missing translations errors
      onError={() => {}}
    >
      {children}
    </IntlProvider>
  )
}

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
const reduxStore = createStore(
  reducer,
  composeEnhancers(applyMiddleware(thunk, shopTempMiddleware))
)

export const App: FC = () => (
  <AppWithMonitoring>
    <ReduxProvider store={reduxStore}>
      <Router>
        <QueryClientProvider client={queryClient}>
          <AppContextProvider>
            <AppIntl>
              <AppCore />
            </AppIntl>
          </AppContextProvider>

          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Router>
    </ReduxProvider>
  </AppWithMonitoring>
)
